import { useDispatch, useSelector } from 'react-redux';
import linq from 'linq';

import useCurrentDb from '../hooks/useCurrentDb';

import { RootState } from '../redux/rootReducer';
import { SheetKinds } from '../redux/sheetDbSlice';
import { CardKinds } from '../redux/cardTemplateDbSlice';
import { CardTemplateAttributeDb } from '../redux/cardTemplateAttributeDbSlice';
import { CardTemplateShapeDb } from '../redux/cardTemplateShapeDbSlice';
import { CardBaseProps, StoryCard, MapCard, CardDb, CardAddPayload, generateCardId, isStoryCard, isCommonCard, isMemoCard, isUiCard, isDrawCard, updateCard } from '../redux/cardDbSlice';
import { CardAttributeDb } from '../redux/cardAttributeDbSlice';
import { CardShapeDb } from '../redux/cardShapeDbSlice';
import { MemoCardContentDb } from '../redux/memoCardContentDbSlice';
import { DrawCardContentDb } from '../redux/drawCardContentDbSlice';
import { ArrowDb, addArrow, updateArrow, deleteArrow } from '../redux/arrowDbSlice';
import { updateLatelyCardTemplate } from '../redux/latelyCardTemplateDbSlice';

import { initial } from '../card_components';
import { getAttributeInitial, AddCardValueProps, addCardValue } from '../card_components/index';
import useLatelyCardTemplates from '../hooks/useLatelyCardTemplates';
import { CoordinateType, makeLately } from '../general';

const useAddCardExe = () => {
  const dispatch = useDispatch();
  const cardTemplateDb = useSelector((state: RootState) => state.cardTemplateDb);
  const cardTemplateAttributes = useSelector((state: RootState) => state.cardTemplateAttributeDb).cardTemplateAttributes;
  const cardTemplateShapes = useSelector((state: RootState) => state.cardTemplateShapeDb).cardTemplateShapes;
  const [sheetId] = useCurrentDb();
  const [latelyIds, , latelyCardTemplateId] = useLatelyCardTemplates();

  const getCardKind = (id: string): CardKinds => {
    const kind = cardTemplateDb.cardTemplates[id].kind;
    return kind;
  }
  const getCardDefaultWidth = (id: string): number => {
    const defaultWidth = cardTemplateDb.cardTemplates[id].defaultWidth;
    return defaultWidth;
  }
  const getCardDefaultHeight = (id: string): number => {
    const defaultHeight = cardTemplateDb.cardTemplates[id].defaultHeight;
    return defaultHeight;
  }
  const getCardFrame = (id: string): {display: boolean, color: string} => {
    const frame = cardTemplateDb.cardTemplates[id].frame;
    return frame;
  }
  const getCardBackground = (id: string): {display: boolean, color: string} => {
    const background = cardTemplateDb.cardTemplates[id].background;
    return background;
  }
  const getCardShadow = (id: string): boolean => {
    const shadow = cardTemplateDb.cardTemplates[id].shadow;
    return shadow;
  }
  const getCardIsGroup = (id: string) => {
    const isGroup = cardTemplateDb.cardTemplates[id].isGroup;
    return isGroup;
  }
  const getCardTemplateItems = (cardTemplateId: string): [string[], CardTemplateAttributeDb[], string[], CardTemplateShapeDb[]] => {
    const attributeIds = Object.keys(cardTemplateAttributes).filter(id => cardTemplateAttributes[id].cardTemplateId === cardTemplateId);
    const shapeIds = Object.keys(cardTemplateShapes).filter(id => cardTemplateShapes[id].cardTemplateId === cardTemplateId);
    const attributeValues = attributeIds.map(id => cardTemplateAttributes[id]);
    const shapeValues = shapeIds.map(id => cardTemplateShapes[id]);
    return [attributeIds, attributeValues, shapeIds, shapeValues];
  }
  const isStoryRootCard = (card: CardDb): card is StoryCard => {
    return card.sheetKind === 'story' && !card.parentId;
  }

  type AddCardExeProps = {
    parent?: SheetKinds | string;
    selectingCardId?: string;
    selectingCardValue?: CardDb;
    ids: string[];
    cards: CardDb[];
    index?: number;
    coordinate?: CoordinateType;
    grid?: CoordinateType;
    clearSelectingGrid?: () => void;
    cutInMode?: boolean;
    arrowId?: string | undefined;
    arrowValue?: ArrowDb;
    startCard?: MapCard;
    endCard?: MapCard;
    arrowIds?: string[];
    arrows?: ArrowDb[];
  }
  const addCardExe = (props: AddCardExeProps) => {
    const { parent, selectingCardId, selectingCardValue, ids, cards, index, coordinate, grid, clearSelectingGrid, cutInMode, arrowId, arrowValue, startCard, endCard, arrowIds, arrows } = props;

    if (!sheetId || selectingCardId === undefined) return;

    const newCardId = generateCardId();
    const [templateAttributeIds, templateAttributes, templateShapeIds, templateShapes] = getCardTemplateItems(selectingCardId);
    type NewCardBaseProps = CardBaseProps & { id: string }
    const newCardBase: NewCardBaseProps = 
      { id: newCardId, sheetId: sheetId, cardTemplateId: selectingCardId, kind: getCardKind(selectingCardId),
        defaultWidth: getCardDefaultWidth(selectingCardId), defaultHeight: getCardDefaultHeight(selectingCardId),
        frame: getCardFrame(selectingCardId), background: getCardBackground(selectingCardId), shadow: getCardShadow(selectingCardId),
        isGroup: getCardIsGroup(selectingCardId), groupParent: undefined
      };

    const getContent = (newCardId: string, newCard: CardAddPayload): AddCardValueProps | undefined => {
      if (isCommonCard(newCard)) {
        const items: CardAttributeDb[] = templateAttributeIds.map((id, ix) => getAttributeInitial(newCardId, id, templateAttributes[ix]));
        const shapes: CardShapeDb[] = templateShapeIds.map((id, ix) => {
          const {cardTemplateId, ...obj} = templateShapes[ix];
          return {cardId: newCardId, cardTemplateShapeId: id, ...obj};
        })
        return {dispatch: dispatch, card: newCard, items: items, shapes: shapes};
      } else if (isMemoCard(newCard)) {
        const memoContent: MemoCardContentDb = {cardId: newCardId, content: ''};
        return {dispatch: dispatch, card: newCard, memoContent: memoContent};
      } else if (isUiCard(newCard)) {
        return {dispatch: dispatch, card: newCard};
      } else if (isDrawCard(newCard)) {
        const drawContent: DrawCardContentDb = {cardId: newCardId, content: ''};
        return {dispatch: dispatch, card: newCard, drawContent: drawContent};
      }
    }

    if (parent === 'story' && index !== undefined) {
      let targetIndex = index + 1;
      for (let i = 0; i < ids.length; i++) {
        const card = cards[i];
        if (isStoryRootCard(card) && card.order === targetIndex) {
          dispatch(updateCard({id: ids[i], order: targetIndex + 1}));
          targetIndex++;
        }
      }
      const newCard: CardAddPayload = {...newCardBase, sheetKind: 'story', order: index + 1, headerText: ''};
      const cardContent = getContent(newCardId, newCard);
      addCardValue(cardContent);
    } else if (parent === 'story' && index === undefined) {
      const newCard: CardAddPayload = {...newCardBase, sheetKind: 'story', order: ids.length || 0, headerText: ''};
      const cardContent = getContent(newCardId, newCard);
      addCardValue(cardContent);
    } else if (parent === 'map') {
      let x = coordinate ? coordinate.x : 0;
      let y = coordinate ? coordinate.y : 0;
      const noGroupLength = linq.from(cards).where(card => !card.isGroup && !card.groupParent).toArray().length;
      const groupLength = linq.from(cards).where(card => card.isGroup && !card.groupParent).toArray().length;
      let z = newCardBase.isGroup ? groupLength : noGroupLength;
      if (cutInMode) {// 矢印カットインの場合は座標指定
        if (startCard && endCard) {
          x = (startCard.x + endCard.x) / 2;
          y = (startCard.y + endCard.y) / 2;
        }
      }
      const newCard: CardAddPayload = {...newCardBase, sheetKind: 'map', x: x, y: y, z: z};
      const cardContent = getContent(newCardId, newCard);
      addCardValue(cardContent);
      if (cutInMode && arrowId && arrowValue && arrowIds && arrows) {// 矢印カットインの場合、矢印を2つに分割
        const updateArrowOrder = () => {
          if (arrowIds.length === 0) return;
          const nomineeIds: string[] = [];
          const nominees: ArrowDb[] = [];
          arrowIds.forEach((targetId, ix) => {
            const value = arrows[ix];
            if (value.fromId === arrowValue.toId || value.toId === arrowValue.toId) {
              nomineeIds.push(targetId);
              nominees.push(value);
            }
          })
          if (nomineeIds.length === 0) return;
          nomineeIds.forEach((targetId, ix) => {
            const value = nominees[ix];
            if (value.order > arrowValue.order) dispatch(updateArrow({ id: targetId, order: value.order - 1 }));
          })
        }
        updateArrowOrder();
        dispatch(deleteArrow({ id: arrowId }));
        dispatch(addArrow({ ...initial.arrow,
                            sheetId: sheetId, fromId: arrowValue.fromId, toId: newCardId,
                            thickness: arrowValue.thickness, color: arrowValue.color, lineStyle: arrowValue.lineStyle, return: arrowValue.return,
                            shapes: arrowValue.shapes, labels: arrowValue.labels, imageId: arrowValue.imageId, notes: arrowValue.notes
                          }));
        dispatch(addArrow({ ...initial.arrow,
                            sheetId: sheetId, fromId: newCardId, toId: arrowValue.toId,
                            thickness: arrowValue.thickness, color: arrowValue.color, lineStyle: arrowValue.lineStyle, return: arrowValue.return,
                            shapes: arrowValue.shapes, labels: arrowValue.labels, imageId: arrowValue.imageId, notes: arrowValue.notes
                          }));
      }
    } else if (parent === 'grid' && grid) {
      const newCard: CardAddPayload = {...newCardBase, sheetKind: 'grid', x: grid.x * 220, y: grid.y * 144, column: grid.x, row: grid.y};
      const cardContent = getContent(newCardId, newCard);
      addCardValue(cardContent);
      if (clearSelectingGrid) clearSelectingGrid();
    } else if (parent && isStoryCard(selectingCardValue)) {
      let brotherIds: string[] = [];
      let brotherValues: StoryCard[] = [];
      ids.forEach((id, ix) => {
        const card = cards[ix];
        if (isStoryCard(card) && card.parentId === parent) {
          brotherIds.push(id);
          brotherValues.push(card);
        }
      })
      const newCard: CardAddPayload = { ...newCardBase, sheetKind: 'story', parentId: parent, order: 0, headerText: '' };
      if (selectingCardValue.parentId === parent) {
        newCard.order = selectingCardValue.order + 1;
        brotherIds.forEach((id, ix) => {
          const card = brotherValues[ix];
          if (card.order > selectingCardValue.order) dispatch(updateCard({ id, order: card.order + 1 }));
        })
      } else {
        brotherIds.forEach((id, ix) => dispatch(updateCard({ id, order: brotherValues[ix].order + 1 })))
      }
      const cardContent = getContent(newCardId, newCard);
      addCardValue(cardContent);
    }

    const newLatelyIds = makeLately(latelyIds, selectingCardId, 'add');
    dispatch(updateLatelyCardTemplate({ id: latelyCardTemplateId, cardTemplateIds: newLatelyIds }));
  }

  return addCardExe;
}

export default useAddCardExe;
